import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface UploadDiagnosticBatch {
  id: string;
  widget: string;
  startedAtIso: string;
  startedAtMs: number;
  fileCount: number;
  jobId: any;
  subjectId: any;
  route: string;
}

export interface UploadDiagnosticFile {
  id: string;
  batchId: string;
  widget: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileIndex: number;
  fileCount: number;
  startedAtIso: string;
  startedAtMs: number;
  stages: { [key: string]: number };
}

@Injectable({ providedIn: 'root' })
export class UploadDiagnosticsService {
  private readonly prefix = '[UPLOAD_DIAG]';

  get enabled(): boolean {
    const envValue = (environment as any).uploadDiagnosticsEnabled;
    if (envValue === false) {
      return false;
    }

    try {
      return envValue === true || window.localStorage.getItem('uploadDiagnostics') !== '0';
    } catch {
      return true;
    }
  }

  startBatch(widget: string, files: FileList | File[] | any, context: any): UploadDiagnosticBatch {
    const batch: UploadDiagnosticBatch = {
      id: this.newId('batch'),
      widget,
      startedAtIso: new Date().toISOString(),
      startedAtMs: this.now(),
      fileCount: files ? files.length || 0 : 0,
      jobId: context ? context.jobId : null,
      subjectId: context ? context.subjectId : null,
      route: context ? context.route : ''
    };

    this.log('batch_start', batch);
    return batch;
  }

  startFile(batch: UploadDiagnosticBatch, file: File, fileIndex: number, source: string): UploadDiagnosticFile {
    const item: UploadDiagnosticFile = {
      id: this.newId('file'),
      batchId: batch ? batch.id : this.newId('batch-missing'),
      widget: batch ? batch.widget : 'unknown-upload-widget',
      fileName: file ? file.name : '',
      fileSize: file ? file.size : 0,
      fileType: file ? file.type : '',
      fileIndex,
      fileCount: batch ? batch.fileCount : 0,
      startedAtIso: new Date().toISOString(),
      startedAtMs: this.now(),
      stages: {}
    };

    this.markFileStage(item, 'file_start', { source });
    return item;
  }

  markBatchStage(batch: UploadDiagnosticBatch, stage: string, extra?: any): void {
    if (!this.enabled || !batch) {
      return;
    }

    this.log(stage, {
      batchId: batch.id,
      elapsedMs: this.elapsed(batch.startedAtMs),
      extra
    });
  }

  markFileStage(item: UploadDiagnosticFile, stage: string, extra?: any): void {
    if (!this.enabled || !item) {
      return;
    }

    item.stages[stage] = this.elapsed(item.startedAtMs);
    this.log(stage, {
      uploadDiagnosticId: item.id,
      batchId: item.batchId,
      fileName: item.fileName,
      fileIndex: item.fileIndex,
      fileCount: item.fileCount,
      elapsedMs: item.stages[stage],
      extra
    });
  }

  markError(item: UploadDiagnosticFile, stage: string, error: any): void {
    this.markFileStage(item, stage, {
      message: error && error.message ? error.message : String(error),
      status: error && error.status ? error.status : null
    });
  }

  headers(baseHeaders: HttpHeaders, item: UploadDiagnosticFile): HttpHeaders {
    if (!this.enabled || !item) {
      return baseHeaders;
    }

    return baseHeaders
      .set('X-Upload-Diagnostic-Id', item.id)
      .set('X-Upload-Batch-Id', item.batchId)
      .set('X-Upload-Widget', item.widget)
      .set('X-Upload-File-Name', item.fileName)
      .set('X-Upload-File-Index', String(item.fileIndex))
      .set('X-Upload-File-Count', String(item.fileCount));
  }

  forLog(item: UploadDiagnosticFile, stage: string): any {
    if (!item) {
      return null;
    }

    return {
      upload_diagnostic_id: item.id,
      upload_batch_id: item.batchId,
      upload_widget: item.widget,
      upload_file_name: item.fileName,
      upload_file_index: item.fileIndex,
      upload_file_count: item.fileCount,
      upload_stage: stage,
      upload_elapsed_ms: this.elapsed(item.startedAtMs),
      upload_started_at: item.startedAtIso
    };
  }

  extractFromSystemLogModel(model: any): any {
    if (!model || !model.log_content) {
      return null;
    }

    try {
      const parsed = typeof model.log_content === 'string'
        ? JSON.parse(model.log_content)
        : model.log_content;
      return parsed ? parsed.upload_diagnostic : null;
    } catch {
      return null;
    }
  }

  markSystemLogStage(uploadDiagnostic: any, stage: string, extra?: any): void {
    if (!this.enabled || !uploadDiagnostic) {
      return;
    }

    this.log(stage, {
      uploadDiagnosticId: uploadDiagnostic.upload_diagnostic_id,
      batchId: uploadDiagnostic.upload_batch_id,
      fileName: uploadDiagnostic.upload_file_name,
      uploadStage: uploadDiagnostic.upload_stage,
      extra
    });
  }

  private newId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  }

  private now(): number {
    if (typeof performance !== 'undefined' && performance.now) {
      return performance.now();
    }

    return Date.now();
  }

  private elapsed(startedAtMs: number): number {
    return Math.round(this.now() - startedAtMs);
  }

  private log(stage: string, data: any): void {
    if (!this.enabled) {
      return;
    }

    console.info(this.prefix, stage, data);
  }
}
